import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Space, Table, Button, Input, Popconfirm, Spin } from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import type { InputRef } from "antd";
import type { FilterConfirmProps } from "antd/es/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import { User, UserStatus } from "../../models/User";
import { RoleType } from "../../models/Role";
import { getAllUsers, deleteUser } from "../../services/UserService";
import "./UserListPage.scss";
import { Toast } from "primereact/toast";
import CustomButton from "../../components/AccommodationData/CustomButton/CustomButton";
///// REDUX IMPORT
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage } from "../../store-redux/slices/ChangePageSlice";

const UserListPage = () => {
  const navigate = useNavigate();
  const { setBreadcrumb } = useApplicationContext();

  const [userList, setUserList] = useState<User[]>([]);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [deleteId, setDeleteId] = useState("");
  const [activeSpinner, setActiveSpinner] = useState(false);
  const searchInput = useRef<InputRef>(null);
  const toast = useRef<any>(null);
  const [pageNumber, setPageNumber] = useState<any>(1);
  const [paginationNumber, setPaginationNumber] = useState<any>(30);

  ///// IMPLEMENTATION REDUX
  const dispatch = useDispatch();
  const currentPage = useSelector((state: any) => state.changePage.currentPage);
  ///// END IMPLEMENTATION REDUX

  useEffect(() => {
    setBreadcrumb(["Podešavanja", "Lista korisnika"]);
    fetchAllUsers();
  }, []);

  const fetchAllUsers = () => {
    setActiveSpinner(true);
    getAllUsers()
      .then((response) => {
        setUserList(response.data.result);
        setPageNumber(currentPage); /// - SET NUMBER PAGE FROM STORE
      })
      .finally(() => setActiveSpinner(false))
      .catch((err) => console.log(err));
  };

  type DataIndex = keyof User;

  const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<User> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Pretraži po imenu`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, border: "none" }}
          >
            Pretraži
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              close();
              setSearchedColumn("");
            }}
            size="small"
            style={{ width: 90 }}
          >
            Poništi
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#C7363D" : undefined }} />
    ),
    onFilter: (value, record: any) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
            color: "#0D4077",
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: ColumnsType<User> = [
    {
      title: "Red. broj",
      width: 100,
      dataIndex: "nameAndSurname",
      key: "nameAndSurname",
      render: (nameAndSurname, text, index) => {
        return (
          <span className="blue-text">
            {(pageNumber - 1) * paginationNumber + index + 1}
          </span>
        );
      },
    },
    {
      title: "Ime i prezime",
      dataIndex: "nameAndSurname",
      key: "nameAndSurname",
      sorter: (a, b) => (a.nameAndSurname < b.nameAndSurname ? -1 : 1),
      defaultSortOrder: "ascend",
      ...getColumnSearchProps("nameAndSurname"),
    },
    {
      title: "Tip korisnika",
      dataIndex: "role",
      key: "role",
      sorter: (a, b) =>
        (a?.role?.name ?? "") < (b?.role?.name ?? "") ? -1 : 1,
      filters: [
        {
          text: RoleType.Admin,
          value: RoleType.Admin,
        },
        {
          text: RoleType.Agency,
          value: RoleType.Agency,
        },
        {
          text: RoleType.Employee,
          value: RoleType.Employee,
        },
      ], //@ts-ignore
      onFilter: (value: string, record) => record?.role?.name == value,
      render: (role) => <span className="blue-text">{role.name}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => ((a.status ?? "") < (b.status ?? "") ? -1 : 1),
      render: (status) => (
        <div className="status-container">
          <span
            className={
              status === UserStatus.Active
                ? "status-dot"
                : "status-dot status-dot-unactive"
            }
          >
            _
          </span>
          <span className="blue-text">{status}</span>
        </div>
      ),
    },
    {
      title: "Opcije",
      fixed: "right",
      width: 260,
      align: "center",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <span
            className="action-button"
            onClick={() => handleEditButton(record.id as string)}
          >
            Izmena
          </span>
          <span
            className="action-button"
            onClick={() => handleViewButton(record.id as string)}
          >
            Pregled
          </span>
          <Popconfirm
            title="Brisanje korisnika?"
            description="Da li ste sigurni da želite da izbrišete korisnika?"
            onConfirm={() => {
              setActiveSpinner(true);
              deleteUser(deleteId)
                .then((response) => {
                  toast?.current?.show({
                    severity: "success",
                    detail: "Uspešno izbrisan korisnik",
                    life: 3000,
                  });
                  fetchAllUsers();
                })
                .finally(() => setActiveSpinner(false))
                .catch((err) => console.log(err));
            }}
            onCancel={() => {}}
            okText="Da"
            cancelText="Ne"
          >
            <span
              className="action-button"
              onClick={() => setDeleteId(record.id as string)}
            >
              Brisanje
            </span>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const handleEditButton = (id: string) => {
    navigate(`/edit-user/${id}`, {
      state: {
        edit: true,
      },
    });
  };

  const handleViewButton = (id: string) => {
    navigate(`/edit-user/${id}`, {
      state: {
        edit: false,
      },
    });
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="user-list">
        <Spin spinning={activeSpinner}>
          <Table
            sticky={true}
            columns={columns}
            scroll={{ x: 1100 }}
            dataSource={userList}
            rowKey={(rowData: any) => rowData.id}
            pagination={{
              current: pageNumber,
              onChange(current) {
                setPageNumber(current);
                dispatch(setCurrentPage(current)); /// DISPATCH ACTION FROM REDUX
              },
              defaultPageSize: 30,
              showSizeChanger: true,
              pageSizeOptions: ["30", "50", "100"],
            }}
            onChange={(pagination, filters, sorter, extra) => {
              setPaginationNumber(pagination.pageSize);
            }}
            className="table-container"
          />
          <div className="button-container">
            <CustomButton
              danger={false}
              htmlType={"submit"}
              key={1}
              type={"primary"}
              function={() => navigate("/new-user")}
              text={"Dodaj korisnika"}
            />
          </div>
        </Spin>
      </div>
    </PageLayout>
  );
};

export default UserListPage;
